'use client'

import clsx from 'clsx'
import { ReactNode } from 'react'
import Button from '../Button/Button'
import Header from '../Header/Header'
import TestimonialsItem, { TestimonialsItemProps } from './TestimonialsItem'

import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import 'swiper/css/a11y'

import { Navigation } from 'swiper/modules'
import Icon from '../Icon/Icon'
import PageSection from '../PageSection/PageSection'

interface TestimonialsButton {
  text: string
  link: {
    href: string
  }
}

export interface TestimonialsProps {
  /**
   * Copy for title/heading
   */
  title: ReactNode
  /**
   * List of testimonials
   */
  testimonials: TestimonialsItemProps[]
  /**
   * Copy for end card title
   */
  endCardTitle?: string
  /**
   * Button for end card CTA
   */
  button?: TestimonialsButton
}

export default function Testimonials({
  title,
  testimonials,
}: TestimonialsProps) {
  return (
    <PageSection>
      <div className="relative pt-6 pb-[88px] bg-white lg:pb-[200px] lg:py-12 overflow-hidden">
        <div className="pl-[44px] pr-6 lg:pl-[100px] lg:pr-0">
          <Header
            as="h2"
            overrideClassName
            className={clsx(
              'type-headline-2 mb-2 text-left lg:type-display-3 lg:mb-20',
            )}
          >
            {title}
          </Header>
        </div>

        <div className="absolute bottom-8 flex justify-center gap-6 w-full pt-2 lg:bottom-12 lg:w-auto lg:pl-36 lg:ml-0 lg:justify-start lg:pt-20">
          <Button
            variant="secondary"
            overrideClassName
            className="button-prev flex border-2 border-theme-primary rounded-full p-2 focus:outline-accent-link focus:outline-offset-4 focus:outline-1 hover:bg-neutral-200 active:bg-neutral-200 disabled:opacity-75 disabled:bg-white disabled:text-neutral-600 disabled:border-neutral-600"
            aria-label="Show previous testimonial"
          >
            <Icon
              icon="arrow-left"
              className="w-7 h-7 text-theme-primary"
            ></Icon>
          </Button>
          <Button
            variant="secondary"
            overrideClassName
            className="button-next flex border-2 border-theme-primary rounded-full p-2 focus:outline-accent-link focus:outline-offset-4 focus:outline-1 hover:bg-neutral-200 active:bg-neutral-200 disabled:opacity-75 disabled:bg-white disabled:text-neutral-600 disabled:border-neutral-600"
            aria-label="Show next testimonial"
          >
            <Icon
              icon="arrow-right"
              className="w-7 h-7 text-theme-primary"
            ></Icon>
          </Button>
        </div>

        <Swiper
          navigation={{
            nextEl: '.button-next',
            prevEl: '.button-prev',
          }}
          slidesPerView={'auto'}
          spaceBetween={8}
          breakpoints={{
            1280: {
              spaceBetween: 120,
            },
          }}
          modules={[Navigation]}
          className="@container !pl-6 lg:!pl-[200px]"
          centeredSlides={false}
          centeredSlidesBounds={false}
        >
          {testimonials.map((testimonial, index) => (
            <SwiperSlide
              key={index}
              className="max-w-[275px] !h-auto self-stretch lg:max-w-[672px]"
            >
              <TestimonialsItem {...testimonial} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </PageSection>
  )
}
